import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import {
	Box,
	Button,
	TextField,
	Autocomplete,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Grid,
	Chip,
	FormControl,
	FormControlLabel,
	Radio,
	RadioGroup,
	FormLabel,
	IconButton,
	Tabs,
	Tab,
	Alert,
} from "@mui/material";
import { Add, Visibility, Delete } from "@mui/icons-material";
import { MaterialReactTable, createMRTColumnHelper } from "material-react-table";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import MDButton from "../../components/MDButton";
import MDTypography from "../../components/MDTypography";
import MDBox from "../../components/MDBox";
import { AuthContext } from "../../context";
import apiService from "../../services/api-service";
import showToast from "../../utils/toast";

const REPORT_TYPES = {
	DNA: { label: "DNA - Dipendente Non Autorizzato", color: "error" },
	BEHAVIORAL: { label: "Segnalazione Comportamentale/Sicurezza", color: "warning" },
};

const columnHelper = createMRTColumnHelper();

function SegnalazioniManagement() {
	const authContext = useContext(AuthContext);

	const [reports, setReports] = useState([]);
	const [loading, setLoading] = useState(false);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [detailDialogOpen, setDetailDialogOpen] = useState(false);
	const [selectedReport, setSelectedReport] = useState(null);
	const [activeTab, setActiveTab] = useState(0);

	const [formData, setFormData] = useState({
		report_type: "DNA",
		supplier_id: null,
		worker_external_id: null,
		worker_name_text: "",
		description: "",
		worker_identification_mode: "dropdown",
	});

	const [suppliers, setSuppliers] = useState([]);
	const [workers, setWorkers] = useState([]);

	const areaId = localStorage.getItem("areaSelected");

	const fetchReports = useCallback(async () => {
		setLoading(true);
		try {
			const params = {};
			if (areaId) params.area_id = areaId;
			const response = await apiService.get("/reports/", params);
			setReports(response || []);
		} catch (error) {
			showToast("Errore caricamento segnalazioni", "error");
		} finally {
			setLoading(false);
		}
	}, [areaId]);

	const fetchSuppliers = useCallback(async () => {
		try {
			const params = {};
			if (areaId) params.area_id = areaId;
			const response = await apiService.get("/reports/options/", params);
			setSuppliers(response.suppliers || []);
		} catch (error) {
			showToast("Errore caricamento fornitori", "error");
		}
	}, [areaId]);

	const fetchWorkers = useCallback(async (supplierId) => {
		if (!supplierId) {
			setWorkers([]);
			return;
		}
		try {
			const response = await apiService.get("/reports/options/", { supplier_id: supplierId });
			setWorkers(response.workers || []);
		} catch (error) {
			showToast("Errore caricamento operatori", "error");
		}
	}, []);

	useEffect(() => {
		fetchReports();
		fetchSuppliers();
	}, [fetchReports, fetchSuppliers]);

	useEffect(() => {
		if (formData.supplier_id) {
			fetchWorkers(formData.supplier_id);
		}
	}, [formData.supplier_id, fetchWorkers]);

	const handleSubmit = async () => {
		if (!formData.supplier_id) {
			showToast("Seleziona un fornitore", "error");
			return;
		}

		try {
			const payload = {
				report_type: formData.report_type,
				supplier_id: formData.supplier_id,
				area_id: areaId,
				description: formData.description,
			};

			if (formData.worker_identification_mode === "dropdown" && formData.worker_external_id) {
				payload.worker_external_id = formData.worker_external_id;
			} else if (formData.worker_name_text) {
				payload.worker_name_text = formData.worker_name_text;
			}

			await apiService.post("/reports/", {}, payload);
			showToast("Segnalazione creata con successo", "success");
			setDialogOpen(false);
			resetForm();
			fetchReports();
		} catch (error) {
			showToast(error.message || "Errore creazione segnalazione", "error");
		}
	};

	const resetForm = () => {
		setFormData({
			report_type: activeTab === 0 ? "DNA" : "BEHAVIORAL",
			supplier_id: null,
			worker_external_id: null,
			worker_name_text: "",
			description: "",
			worker_identification_mode: "dropdown",
		});
		setWorkers([]);
	};

	const handleOpenDialog = (type) => {
		setFormData({
			...formData,
			report_type: type,
		});
		setDialogOpen(true);
	};

	const handleViewDetail = (report) => {
		setSelectedReport(report);
		setDetailDialogOpen(true);
	};

	const handleDelete = async (reportId) => {
		if (!window.confirm("Sei sicuro di voler eliminare questa segnalazione?")) {
			return;
		}
		try {
			await apiService.delete("/reports/", { id: reportId });
			showToast("Segnalazione eliminata", "success");
			fetchReports();
		} catch (error) {
			showToast("Errore eliminazione segnalazione", "error");
		}
	};

	const filteredReports = useMemo(() => {
		const type = activeTab === 0 ? "DNA" : "BEHAVIORAL";
		return reports.filter((r) => r.report_type === type);
	}, [reports, activeTab]);

	const columns = useMemo(
		() => [
			columnHelper.accessor("report_type_display", {
				header: "Tipo",
				size: 150,
				Cell: ({ row }) => (
					<Chip
						label={row.original.report_type_display}
						color={REPORT_TYPES[row.original.report_type]?.color || "default"}
						size="small"
					/>
				),
			}),
			columnHelper.accessor("supplier_name", {
				header: "Fornitore",
				size: 200,
			}),
			columnHelper.accessor("worker_name", {
				header: "Operatore",
				size: 200,
			}),
			columnHelper.accessor("description", {
				header: "Descrizione",
				size: 300,
				Cell: ({ cell }) => (
					<span title={cell.getValue()}>
						{cell.getValue()?.length > 50 ? cell.getValue().substring(0, 50) + "..." : cell.getValue() || "-"}
					</span>
				),
			}),
			columnHelper.accessor("created_at", {
				header: "Data",
				size: 150,
				Cell: ({ cell }) => new Date(cell.getValue()).toLocaleString("it-IT"),
			}),
			columnHelper.accessor("created_by", {
				header: "Creato da",
				size: 150,
			}),
		],
		[]
	);

	return authContext.hasAuth("VIEW_SEGNALAZIONI") ? (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox p={3}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<MDTypography variant="h4" color="dark">
						Segnalazioni
					</MDTypography>
					{authContext.hasAuth("ADD_SEGNALAZIONI") && (
						<Box display="flex" gap={2}>
							<MDButton variant="contained" color="error" startIcon={<Add />} onClick={() => handleOpenDialog("DNA")}>
								DNA
							</MDButton>
							<MDButton
								variant="contained"
								color="warning"
								startIcon={<Add />}
								onClick={() => handleOpenDialog("BEHAVIORAL")}
							>
								Segnalazione Comportamentale
							</MDButton>
						</Box>
					)}
				</Box>

				<Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ mb: 2 }}>
					<Tab label="DNA - Dipendente Non Autorizzato" />
					<Tab label="Segnalazioni Comportamentali" />
				</Tabs>

				<MaterialReactTable
					columns={columns}
					data={filteredReports}
					enableRowActions
					positionActionsColumn="last"
					renderRowActions={({ row }) => (
						<Box display="flex" gap={1}>
							<IconButton size="small" onClick={() => handleViewDetail(row.original)} title="Visualizza">
								<Visibility />
							</IconButton>
							{authContext.hasAuth("DELETE_SEGNALAZIONI") && (
								<IconButton
									size="small"
									onClick={() => handleDelete(row.original.id)}
									title="Elimina"
									color="error"
								>
									<Delete />
								</IconButton>
							)}
						</Box>
					)}
					state={{ isLoading: loading }}
					enableSorting
					enableColumnFilters={false}
					enablePagination
					initialState={{ pagination: { pageSize: 10 } }}
				/>

				<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
					<DialogTitle>
						{formData.report_type === "DNA" ? "Nuova Segnalazione DNA" : "Nuova Segnalazione Comportamentale"}
					</DialogTitle>
					<DialogContent>
						<Alert severity="info" sx={{ mb: 2, mt: 1 }}>
							La segnalazione arriverà al responsabile sicurezza di Bottoli e del fornitore.
						</Alert>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Autocomplete
									options={suppliers}
									getOptionLabel={(option) => option.name || ""}
									value={suppliers.find((s) => s.id === formData.supplier_id) || null}
									onChange={(event, newValue) => {
										setFormData((prev) => ({
											...prev,
											supplier_id: newValue?.id || null,
											worker_external_id: null,
										}));
									}}
									renderInput={(params) => <TextField {...params} label="Fornitore *" required />}
								/>
							</Grid>

							{formData.supplier_id && (
								<Grid item xs={12}>
									<FormControl component="fieldset">
										<FormLabel>Identificazione Operatore</FormLabel>
										<RadioGroup
											row
											value={formData.worker_identification_mode}
											onChange={(e) =>
												setFormData((prev) => ({
													...prev,
													worker_identification_mode: e.target.value,
													worker_external_id: null,
													worker_name_text: "",
												}))
											}
										>
											<FormControlLabel value="dropdown" control={<Radio />} label="Seleziona da lista" />
											<FormControlLabel value="freetext" control={<Radio />} label="Inserisci nome" />
										</RadioGroup>
									</FormControl>
								</Grid>
							)}

							{formData.worker_identification_mode === "dropdown" && formData.supplier_id && (
								<Grid item xs={12}>
									<Autocomplete
										options={workers}
										getOptionLabel={(option) => option.name || ""}
										value={workers.find((w) => w.id === formData.worker_external_id) || null}
										onChange={(event, newValue) => {
											setFormData((prev) => ({
												...prev,
												worker_external_id: newValue?.id || null,
											}));
										}}
										renderInput={(params) => (
											<TextField
												{...params}
												label={formData.report_type === "DNA" ? "Nome Operatore (opzionale)" : "Operatore"}
											/>
										)}
									/>
								</Grid>
							)}

							{formData.worker_identification_mode === "freetext" && formData.supplier_id && (
								<Grid item xs={12}>
									<TextField
										fullWidth
										label={formData.report_type === "DNA" ? "Nome Operatore (opzionale)" : "Nome Operatore"}
										value={formData.worker_name_text}
										onChange={(e) =>
											setFormData((prev) => ({
												...prev,
												worker_name_text: e.target.value,
											}))
										}
									/>
								</Grid>
							)}

							<Grid item xs={12}>
								<TextField
									fullWidth
									multiline
									rows={4}
									label="Descrizione"
									value={formData.description}
									onChange={(e) =>
										setFormData((prev) => ({
											...prev,
											description: e.target.value,
										}))
									}
									placeholder={
										formData.report_type === "DNA"
											? "Descrivi la situazione del dipendente non autorizzato..."
											: "Descrivi l'incidente comportamentale o di sicurezza..."
									}
								/>
							</Grid>
						</Grid>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDialogOpen(false)}>Annulla</Button>
						<Button onClick={handleSubmit} variant="contained" color="primary">
							Crea Segnalazione
						</Button>
					</DialogActions>
				</Dialog>

				<Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
					<DialogTitle>Dettaglio Segnalazione</DialogTitle>
					<DialogContent>
						{selectedReport && (
							<Grid container spacing={2} mt={1}>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Tipo
									</Typography>
									<Chip
										label={selectedReport.report_type_display}
										color={REPORT_TYPES[selectedReport.report_type]?.color}
										size="small"
									/>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Data
									</Typography>
									<Typography>{new Date(selectedReport.created_at).toLocaleString("it-IT")}</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Fornitore
									</Typography>
									<Typography>{selectedReport.supplier_name || "N/D"}</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Operatore
									</Typography>
									<Typography>{selectedReport.worker_name || "N/D"}</Typography>
								</Grid>
								<Grid item xs={12}>
									<Typography variant="body2" color="textSecondary">
										Descrizione
									</Typography>
									<Typography>{selectedReport.description || "Nessuna descrizione"}</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Creato da
									</Typography>
									<Typography>{selectedReport.created_by}</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Notifica inviata
									</Typography>
									<Chip
										label={selectedReport.notification_sent ? "Si" : "No"}
										color={selectedReport.notification_sent ? "success" : "error"}
										size="small"
									/>
								</Grid>
							</Grid>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDetailDialogOpen(false)}>Chiudi</Button>
					</DialogActions>
				</Dialog>
			</MDBox>
		</DashboardLayout>
	) : (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox p={3}>
				<MDTypography>Non hai i permessi per accedere a questa pagina</MDTypography>
			</MDBox>
		</DashboardLayout>
	);
}

export default SegnalazioniManagement;
